import cn from "classnames";

/** Leaflet / inline SVG - matches Tailwind `sky-700` (same as primary buttons) */
export const BRAND_ACCENT_HEX = "#108ed1";

/** Destination pin on Leaflet maps - Google Maps–style red (`#EA4335`); route line stays `BRAND_ACCENT_HEX` */
export const MAP_MARKER_PIN_HEX = "#ea5935";

/** @deprecated Use `BRAND_ACCENT_HEX` */
export const BRAND_SKY_600_HEX = BRAND_ACCENT_HEX;

/** Focus ring - sky matches primary actions site-wide */
export const buttonFocusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2";

/** Dark hero / photo banner behind control */
export const buttonFocusRingOffsetHero = "focus-visible:ring-offset-[#0d0a22]";

/** Navy marketing panels (e.g. newsletter strip) */
export const buttonFocusRingOffsetNavy = "focus-visible:ring-offset-[#0a2547]";

export const buttonDisabled = "disabled:pointer-events-none disabled:opacity-45";

/**
 * Shared minimum height for text buttons (primary, secondary, muted, compact).
 * Keeps CTAs visually aligned across hero, forms, cookies, and about blocks.
 */
export const buttonMinHeight = "min-h-11";

/** Primary CTA - submits, accept, main navigation goals */
export const buttonPrimary = cn(
  "inline-flex items-center justify-center rounded-lg bg-[#108ed1] px-6 text-sm font-medium text-white transition hover:bg-[#0d7ab8] active:scale-[0.98]",
  buttonMinHeight,
  buttonFocusRing
);

export const buttonPrimaryHero = cn(buttonPrimary, buttonFocusRingOffsetHero);

/** Narrow toolbar actions (newsletter field row) - same height as primary */
export const buttonPrimaryCompact = cn(
  "inline-flex shrink-0 items-center justify-center rounded-lg bg-[#108ed1] px-4 text-sm font-medium text-white transition hover:bg-[#0d7ab8] active:scale-[0.98]",
  buttonMinHeight,
  buttonFocusRing,
  buttonFocusRingOffsetNavy
);

/** Newsletter / dark panels */
export const buttonNewsletterSubmit = cn(
  "inline-flex shrink-0 items-center justify-center rounded-lg bg-[#108ed1] px-4 text-sm font-medium text-white transition hover:bg-[#0d7ab8] active:scale-[0.98]",
  buttonMinHeight,
  buttonFocusRing,
  buttonFocusRingOffsetNavy
);

/** Full-width form submit */
export const buttonPrimaryFullWidth = cn(
  buttonPrimary,
  "w-full text-base font-medium shadow-lg shadow-sky-950/20",
  buttonDisabled
);

/** Secondary on dark imagery - outline */
export const buttonSecondaryOnDark = cn(
  "inline-flex items-center justify-center rounded-lg border-2 border-white/35 bg-transparent px-6 text-sm font-medium text-gray-200 transition hover:border-white/55 hover:bg-white/10 hover:text-white active:scale-[0.98]",
  buttonMinHeight,
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0a22]"
);

/** Secondary on light surfaces - outline (phone, alternate choice) */
export const buttonSecondaryOnLight = cn(
  "inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-6 text-center text-sm font-medium text-slate-800 transition hover:border-slate-400 hover:bg-slate-50 active:scale-[0.98]",
  buttonMinHeight,
  buttonFocusRing
);

/** Low-emphasis action on light UI (decline, dismiss-style) */
export const buttonMutedOnLight = cn(
  "inline-flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-100",
  buttonMinHeight,
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
);

/** Icon-only controls - square touch target matching text button height */
export const buttonIconGhost =
  "inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2";

/** Inline link accent (privacy, legal) - same hue family as buttons */
export const linkInlineAccent =
  "font-medium text-sky-800 underline-offset-2 hover:text-sky-900 hover:underline";

/** Shared focus for legacy bordered inputs (home contact snippet, etc.) */
export const formControlFocus =
  "focus:border-sky-600 focus:ring-2 focus:ring-sky-300/80 outline-none";
